/** @format */

import {gql} from '@apollo/client';
import {ALL_ASSETS_MEM, ALL_ASSETS_MEM_ONLY_ID} from './query/AssetQuery';
import {ALL_HISTORY_LOADING} from './query/HistoryButton';
import {FIT_BOUNDS} from './query/MapQuery';

const CLIENT_STATUS = 'ClientStatus';

const ASSET_POS_FRAGMENT = gql`
  fragment AssetPosFields on AssetInMem {
    id
    trigged
    markerVisible
    iconVisible
    stopsVisible
    latLngGrid {
      lat
      lng
      speed
      utime
    }
  }
`;

function ok() {
  return {status: 'ok', __typename: CLIENT_STATUS};
}

function eachAssetId(cache, fn) {
  let data;
  try {
    data = cache.readQuery({query: ALL_ASSETS_MEM_ONLY_ID});
  } catch (e) {
    return;
  }
  if (!data || !data.assetsInMem) return;
  for (let i = 0; i < data.assetsInMem.length; i++) {
    fn(data.assetsInMem[i].id);
  }
}

function assetCacheId(cache, id) {
  return cache.identify({__typename: 'AssetInMem', id});
}

function readAssetPos(cache, id) {
  try {
    return cache.readFragment({
      id: assetCacheId(cache, id),
      fragment: ASSET_POS_FRAGMENT,
    });
  } catch (e) {
    return null;
  }
}

export const typeDefs = `
  type Query {
    assetsInMem: [AssetInMem],
    assetInMem: AssetInMem,
    historyButtonLoader: [assetId: String],
    sideNavs: [SideNav],
    fitBounds: [LatLng],
    modalsDriver: ModalEvent,
    historyPolylinesSetting: [HistoryPolylinesSetting]
  }

  type HistoryPolylinesSetting {
    startUtime: Int,
    stopUtime: Int,
    assetId: String,
    active: Boolean
  }

  type AssetInMem {
    id:		String,
    name:		String,
    sticker:	String,
    totalKm:	Int,
    totalKmCanBus:	Int,
    motohours:	Int,
    marker:		String,
    speed:		Int,
    ignition:	Boolean,
    lastUtime:	String,
    address:	String,
    externalVoltage:	Int,
    gsmSignal:	Int,
    visSat: Int,
    todayKm:	Int,
    todayTime:	Int,
    companyId: String,
    latLngGrid:		[LatLngMem],
    trigged: Boolean,
    markerVisible: Boolean,
    iconVisible: Boolean,
    stopsVisible: Boolean,
  }

  //this can be [fullScreen, smallError, smallWarning, smallInfo]
  type ModalEvent {
    type: String!,
    title: String,
    rand: Float,
    text: String!
  }


  type SideNav {
    name: String!,
    width: Int!,
    minWidth: Int!,
    visible: Boolean
  }

  type LatLngMem {
    id: String,
    lat: Float!,
    lng: Float!,
    speed: Int,
    utime: Int!
  }

  type ClientStatus {
    status: String
  }
`;

export const resolvers = {
  Mutation: {
    mutateCard: (_, {id, action}, {cache}) => {
      if (action === 'checkBoxClicked') {
        cache.modify({
          id: assetCacheId(cache, id),
          fields: {
            markerVisible(existing) {
              return !existing;
            },
          },
        });
        return ok();
      }

      if (action === 'trigged' || action === 'triggedNoFit') {
        const target = readAssetPos(cache, id);
        const nextTrigged = action === 'triggedNoFit' ? true : !(target && target.trigged);

        if (action === 'trigged' && nextTrigged && target && target.latLngGrid && target.latLngGrid.length > 0) {
          try {
            cache.writeQuery({
              query: FIT_BOUNDS,
              data: {
                fitBounds: [
                  {
                    ...currentPosition(target.latLngGrid),
                    __typename: 'LatLng',
                  },
                ],
              },
            });
          } catch (e) {
            // ignore fitBounds failures
          }
        }

        eachAssetId(cache, assetId => {
          const open = assetId === id ? nextTrigged : false;
          cache.modify({
            id: assetCacheId(cache, assetId),
            fields: {
              trigged() {
                return open;
              },
            },
          });
        });
        return ok();
      }

      return ok();
    },
    showHideAllMarkers: (_, {state}, {cache}) => {
      eachAssetId(cache, assetId => {
        cache.modify({
          id: assetCacheId(cache, assetId),
          fields: {
            markerVisible() {
              return state;
            },
          },
        });
      });
      return ok();
    },
    insertAssetIntoCache: async (_, {assetInput}, {client}) => {
      const assetsPrev = await client.readQuery({
        query: ALL_ASSETS_MEM,
      });

      var newAsset = {...assetInput};
      newAsset.__typename = 'AssetInMem';

      const newAssetsArray = [...assetsPrev.assetsInMem, newAsset];
      await client.writeQuery({
        query: ALL_ASSETS_MEM,
        data: {assetsInMem: newAssetsArray},
      });

      return {status: 'done', __typename: CLIENT_STATUS};
    },
    disableIcons: (_, {disableIcons}, {cache}) => {
      eachAssetId(cache, assetId => {
        cache.modify({
          id: assetCacheId(cache, assetId),
          fields: {
            iconVisible() {
              return !disableIcons;
            },
          },
        });
      });
      return ok();
    },
    disableStops: (_, {disableStops}, {cache}) => {
      eachAssetId(cache, assetId => {
        cache.modify({
          id: assetCacheId(cache, assetId),
          fields: {
            stopsVisible() {
              return !disableStops;
            },
          },
        });
      });
      return ok();
    },
    mutateHistoryButtonLoader: (_, {assetId, state}, {cache}) => {
      const loadingState = cache.readQuery({
        query: ALL_HISTORY_LOADING,
      });

      let {historyButtonLoader} = loadingState;
      let index = historyButtonLoader.findIndex(x => x.assetId === assetId);

      if (index === -1 && state === 'startLoading') {
        let newLoadingState = {assetId: assetId};
        newLoadingState.__typename = 'AssetIdLoader';

        let newLoadingStateArray = [...historyButtonLoader, newLoadingState];

        cache.writeQuery({
          query: ALL_HISTORY_LOADING,
          data: {historyButtonLoader: newLoadingStateArray},
        });
      } else if (index > -1 && state === 'done') {
        let newLoadingStateArray = historyButtonLoader.filter(x => x.assetId !== assetId);

        cache.writeQuery({
          query: ALL_HISTORY_LOADING,
          data: {historyButtonLoader: newLoadingStateArray},
        });
      }

      return {status: 'done', __typename: CLIENT_STATUS, assetId};
    },
  },
  Query: {
    assetInMem: (obj, args, {cache}) => {
      try {
        const {assetsInMem} = cache.readQuery({query: ALL_ASSETS_MEM});
        return assetsInMem.find(asset => asset.id === args.id) || null;
      } catch (e) {
        return null;
      }
    },
  },
};

const currentPosition = latLngSeed => {
  // current time - 20 sec
  const now = new Date();
  let currentTime = now.getTime() / 1000 - 20;
  let index = 0;

  while (latLngSeed[index + 1] && latLngSeed[index].utime > currentTime) {
    index++;
  }

  //gps is actual and it is possible to animate the marker
  if (index > 0) {
    let diff = {
      lat: latLngSeed[index - 1].lat - latLngSeed[index].lat,
      lng: latLngSeed[index - 1].lng - latLngSeed[index].lng,
      utime: latLngSeed[index - 1].utime - latLngSeed[index].utime,
    };

    //calc speed
    diff.lat = diff.lat / diff.utime;
    diff.lng = diff.lng / diff.utime;

    //how far is current time
    diff.currentTimeDiff = currentTime - latLngSeed[index].utime;

    //calc position
    diff.currentLat = Number(latLngSeed[index].lat) + diff.lat * diff.currentTimeDiff;
    diff.currentLng = Number(latLngSeed[index].lng) + diff.lng * diff.currentTimeDiff;

    //return current state
    return {
      lat: diff.currentLat,
      lng: diff.currentLng,
      inMotion: true,
      utime: latLngSeed[index].utime,
    };
  }

  //return when nothing to animate (gps time is not actual)
  return {
    lat: latLngSeed[0].lat,
    lng: latLngSeed[0].lng,
    inMotion: false,
    utime: latLngSeed[index].utime,
  };
};
